/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { ProfileSettings } from 'src/components/Profile';
import Placeholder from 'src/components/Profile/ProfileSettings/Placeholder';

import getEmptyRetryVariables from './helpers/getEmptyRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'profile.profile_settings.initial_load_error.message',
    defaultMessage: 'An error occurred while loading your profile information. Please try again.',
  },
});

export const profileSettingsRouteQuery = graphql`
  query ProfileSettingsRoute_Query {
    viewer {
      ...ProfileSettings_viewer
    }
  }
`;

const profileSettingsRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({}),
});

/**
 * Renders `ProfileSettings` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} profile settings route content.
 */
function ProfileSettingsPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(profileSettingsRouteQuery, queryRef);
  return <ProfileSettings {...routeProps} {...queryData} />;
}

ProfileSettingsPreloadedQueryRenderer.propTypes = {
  routeProps: profileSettingsRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `ProfileSettings` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @returns {React.ReactNode} profile settings route content.
 */
function ProfileSettingsRouteRenderer({ routeProps }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    profileSettingsRouteQuery,
    routeProps,
    { logLabel: 'ProfileSettingsRoute', getRetryVariablesFunc: getEmptyRetryVariables },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={Placeholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {queryRef ? (
          <ProfileSettingsPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <ProfileSettings {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

ProfileSettingsRouteRenderer.propTypes = {
  routeProps: profileSettingsRoutePropsShape.isRequired,
};

export default ProfileSettingsRouteRenderer;
